/**
 * Color System
 * Semantic colors built from primitive Camino colors with light/dark mode support
 * Uses branded types to prevent direct string access
 */

import colors from "../../theme/colors";
import cdsColors from "../../theme/cdsColors";

// Branded type system to prevent direct access
declare const __text_color_brand: unique symbol;
declare const __surface_color_brand: unique symbol;
declare const __border_color_brand: unique symbol;
declare const __interactive_color_brand: unique symbol;
declare const __primitive_color_brand: unique symbol;
declare const __inverted_color_brand: unique symbol;

export interface TextColorToken {
  readonly [__text_color_brand]: true;
  readonly _semantic: string;
}

export interface SurfaceColorToken {
  readonly [__surface_color_brand]: true;
  readonly _semantic: string;
}

export interface BorderColorToken {
  readonly [__border_color_brand]: true;
  readonly _semantic: string;
}

export interface InteractiveColorToken {
  readonly [__interactive_color_brand]: true;
  readonly _semantic: string;
}

export interface PrimitiveColorToken {
  readonly [__primitive_color_brand]: true;
  readonly _hue: string;
  readonly _scale?: number;
}

export interface InvertedColorToken {
  readonly [__inverted_color_brand]: true;
  readonly _originalToken: TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken;
  readonly _semantic: "inverted";
}

// Internal factory functions
const createTextColorToken = (semantic: string): TextColorToken => ({ _semantic: semantic } as TextColorToken);

const createSurfaceColorToken = (semantic: string): SurfaceColorToken => ({ _semantic: semantic } as SurfaceColorToken);

const createBorderColorToken = (semantic: string): BorderColorToken => ({ _semantic: semantic } as BorderColorToken);

const createInteractiveColorToken = (semantic: string): InteractiveColorToken => ({ _semantic: semantic } as InteractiveColorToken);

const createPrimitiveColorToken = (hue: string, scale?: number): PrimitiveColorToken => ({ _hue: hue, _scale: scale } as PrimitiveColorToken);


// Semantic color mappings that use your existing Camino colors
const semanticColorMap = {
  // Text colors mapped to your existing theme
  text: {
    primary: { light: cdsColors.cdsGrey[800], dark: "#FFFFFF" },
    secondary: { light: cdsColors.cdsGrey[500], dark: "#E5E5E5" },
    tertiary: { light: colors.valueBlack.DEFAULT, dark: "#CCCCCC" },
    quaternary: { light: colors.searchBarGrey.DEFAULT, dark: "#999999" },
    placeholder: { light: colors.searchBarGrey.DEFAULT, dark: "#777777" },
    inverse: { light: "#FFFFFF", dark: cdsColors.cdsGrey[800] },
    disabled: { light: colors.disabledGray.text, dark: "#666666" },
    link: { light: colors.linkBlue.DEFAULT, dark: colors.linkBlue.DEFAULT },
    linkHover: { light: colors.alertBlue[700], dark: colors.alertBlue[400] },
    success: { light: cdsColors.cdsGreen[800], dark: cdsColors.cdsGreen[400] },
    warning: { light: cdsColors.cdsOrange[800], dark: cdsColors.cdsOrange[400] },
    error: { light: colors.caminoNewErrorRed[600], dark: colors.caminoNewErrorRed[400] },
  },

  // Surface colors
  surface: {
    primary: { light: "#FFFFFF", dark: "#1A1A1A" },
    secondary: { light: colors.creamBg.DEFAULT, dark: "#2D2D2D" },
    tertiary: { light: cdsColors.cdsGrey[50], dark: "#3A3A3A" },
    quaternary: { light: colors.resolveGrey.DEFAULT, dark: "#4A4A4A" },
    elevated: { light: "#FFFFFF", dark: "#2A2A2A" },
    overlay: { light: "rgba(0, 0, 0, 0.5)", dark: "rgba(0, 0, 0, 0.7)" },
    input: { light: "#FFFFFF", dark: "#333333" },
    disabled: { light: colors.disabledGray.DEFAULT, dark: "#555555" },
    success: { light: colors.newHireGreen.bg, dark: cdsColors.cdsGreen[900] },
    warning: { light: colors.dateYellow.background, dark: cdsColors.cdsOrange[900] },
    error: { light: colors.statusRed.bg, dark: colors.caminoNewErrorRed[900] },
  },

  // Border colors
  border: {
    primary: { light: colors.tableBorder.DEFAULT, dark: "#4A4A4A" },
    secondary: { light: colors.tableUnderlineBorder.DEFAULT, dark: "#3A3A3A" },
    tertiary: { light: colors.trixBorderGrey, dark: "#2A2A2A" },
    focus: { light: cdsColors.brand[500], dark: cdsColors.brand[400] },
    disabled: { light: colors.disabledGray.DEFAULT, dark: "#555555" },
    success: { light: cdsColors.cdsGreen[400], dark: cdsColors.cdsGreen[600] },
    warning: { light: cdsColors.cdsOrange[400], dark: cdsColors.cdsOrange[600] },
    error: { light: colors.caminoNewErrorRed[400], dark: colors.caminoNewErrorRed[600] },
  },

  // Interactive colors
  interactive: {
    primary: { light: cdsColors.brand[500], dark: cdsColors.brand[400] },
    primaryHover: { light: cdsColors.brand[600], dark: cdsColors.brand[300] },
    primaryActive: { light: cdsColors.brand[700], dark: cdsColors.brand[200] },
    secondary: { light: cdsColors.cdsGreen[500], dark: cdsColors.cdsGreen[400] },
    secondaryHover: { light: cdsColors.cdsGreen[600], dark: cdsColors.cdsGreen[300] },
    secondaryActive: { light: cdsColors.cdsGreen[700], dark: cdsColors.cdsGreen[200] },
    tertiary: { light: cdsColors.cdsGrey[600], dark: cdsColors.cdsGrey[900] },
    tertiaryHover: { light: cdsColors.cdsGrey[700], dark: cdsColors.cdsGrey[800] },
    tertiaryActive: { light: cdsColors.cdsGrey[700], dark: cdsColors.cdsGrey[700] },
  },
} as const;

// Primitive color mappings from your theme
const primitiveColorMap = {
  // Core brand colors
  brand: cdsColors.brand,

  // Camino palette
  green: cdsColors.cdsGreen,
  red: colors.caminoNewErrorRed,
  orange: cdsColors.cdsOrange,
  indigo: cdsColors.cdsIndigo,
  purple: cdsColors.cdsIndigo,
  yellow: cdsColors.cdsOrange,

  // Neutrals
  gray: cdsColors.cdsGrey,

  // Specialized colors
  lilac: cdsColors.cdsIndigo,
  groupBlue: colors.groupBlue,
  singlePink: colors.singlePink,
} as const;

// Public API
export const Color = {
  text: {
    primary: createTextColorToken("text.primary"),
    secondary: createTextColorToken("text.secondary"),
    tertiary: createTextColorToken("text.tertiary"),
    quaternary: createTextColorToken("text.quaternary"),
    placeholder: createTextColorToken("text.placeholder"),
    inverse: createTextColorToken("text.inverse"),
    disabled: createTextColorToken("text.disabled"),
    link: createTextColorToken("text.link"),
    linkHover: createTextColorToken("text.linkHover"),
    success: createTextColorToken("text.success"),
    warning: createTextColorToken("text.warning"),
    error: createTextColorToken("text.error"),
  } as const,

  surface: {
    primary: createSurfaceColorToken("surface.primary"),
    secondary: createSurfaceColorToken("surface.secondary"),
    tertiary: createSurfaceColorToken("surface.tertiary"),
    quaternary: createSurfaceColorToken("surface.quaternary"),
    elevated: createSurfaceColorToken("surface.elevated"),
    overlay: createSurfaceColorToken("surface.overlay"),
    input: createSurfaceColorToken("surface.input"),
    disabled: createSurfaceColorToken("surface.disabled"),
    success: createSurfaceColorToken("surface.success"),
    warning: createSurfaceColorToken("surface.warning"),
    error: createSurfaceColorToken("surface.error"),
  } as const,

  border: {
    primary: createBorderColorToken("border.primary"),
    secondary: createBorderColorToken("border.secondary"),
    tertiary: createBorderColorToken("border.tertiary"),
    focus: createBorderColorToken("border.focus"),
    disabled: createBorderColorToken("border.disabled"),
    success: createBorderColorToken("border.success"),
    warning: createBorderColorToken("border.warning"),
    error: createBorderColorToken("border.error"),
  } as const,

  interactive: {
    primary: createInteractiveColorToken("interactive.primary"),
    primaryHover: createInteractiveColorToken("interactive.primaryHover"),
    primaryActive: createInteractiveColorToken("interactive.primaryActive"),
    secondary: createInteractiveColorToken("interactive.secondary"),
    secondaryHover: createInteractiveColorToken("interactive.secondaryHover"),
    secondaryActive: createInteractiveColorToken("interactive.secondaryActive"),
    tertiary: createInteractiveColorToken("interactive.tertiary"),
    tertiaryHover: createInteractiveColorToken("interactive.tertiaryHover"),
    tertiaryActive: createInteractiveColorToken("interactive.tertiaryActive"),
  } as const,

  primitive: {
    // Core colors with scale support
    brand: (scale?: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950) => createPrimitiveColorToken("brand", scale),
    green: (scale?: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900) => createPrimitiveColorToken("green", scale),
    red: (scale?: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950) => createPrimitiveColorToken("red", scale),
    orange: (scale?: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900) => createPrimitiveColorToken("orange", scale),
    blue: (scale?: 50 | 100 | 200 | 300) => createPrimitiveColorToken("blue", scale),
    indigo: (scale?: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900) => createPrimitiveColorToken("indigo", scale),
    purple: () => createPrimitiveColorToken("purple"),
    yellow: () => createPrimitiveColorToken("yellow"),
    gray: (scale?: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950) => createPrimitiveColorToken("gray", scale),

    // Specialized colors
    lilac: () => createPrimitiveColorToken("lilac"),
    groupBlue: (scale?: 50 | 100 | 200 | 400) => createPrimitiveColorToken("groupBlue", scale),
    singlePink: (scale?: 50 | 100 | 200 | 400) => createPrimitiveColorToken("singlePink", scale),
  } as const,
} as const;

// Helper to detect if we're in dark mode
// Note: This is a fallback for when outside React context.
// In React components, prefer using useColorMode() from Chakra UI
const isDarkMode = (): boolean => {
  if (typeof window === "undefined") return false;

  // First check if Chakra UI has set a color mode in localStorage
  try {
    const chakraColorMode = localStorage.getItem("chakra-ui-color-mode");
    if (chakraColorMode === "dark") return true;
    if (chakraColorMode === "light") return false;
  } catch {
    // Fallback if localStorage is not available
  }

  // Fall back to system preference
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
};

// Internal function to resolve color tokens to actual color values
// This is the old implementation that will be replaced by resolveColorWithMode

// Export the color mappings for use in CSS generation or theme setup
export { semanticColorMap, primitiveColorMap };

// Enhanced color resolution that works with Chakra UI's color mode
// This function will be used internally by components to get the right color for the current mode
export const resolveColorWithMode = (
  token: TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken | PrimitiveColorToken,
  colorMode?: "light" | "dark",
): string => {
  // Handle primitive colors (these don't change with color mode)
  if (token && typeof token === "object" && "_hue" in token) {
    const colorMap = primitiveColorMap[token._hue as keyof typeof primitiveColorMap];
    if (!colorMap) return "transparent";

    if (token._scale !== undefined) {
      // Return specific scale
      return (colorMap as any)[token._scale] || (colorMap as any).DEFAULT || "transparent";
    }
    // Return default
    return (colorMap as any).DEFAULT || (colorMap as any)[500] || "transparent";
  }

  // Handle semantic colors with light/dark mode
  if (token && typeof token === "object" && "_semantic" in token) {
    const [category, name] = token._semantic.split(".");
    const semanticMap = semanticColorMap[category as keyof typeof semanticColorMap];

    if (!semanticMap || !semanticMap[name as keyof typeof semanticMap]) {
      return "transparent";
    }

    const colorDef = semanticMap[name as keyof typeof semanticMap] as { light: string; dark: string };

    // Use provided colorMode or detect it
    const currentMode = colorMode || (isDarkMode() ? "dark" : "light");
    return currentMode === "dark" ? colorDef.dark : colorDef.light;
  }

  return "transparent";
};

// Backward compatibility - uses auto-detection
export const resolveColor = (
  token: TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken | PrimitiveColorToken,
): string => resolveColorWithMode(token);
